import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

interface AppSettings {
  language?: string;
  timezone?: string;
  date_format?: string;
  currency_format?: string;
  [key: string]: unknown;
}

export function Dashboard() {
  const { user, plan, refreshSession, logout } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [settings, setSettings] = useState<AppSettings>({});
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [loginHistory, setLoginHistory] = useState<Array<{
    device_info?: any;
    ip?: string;
    country?: string;
    city?: string;
    region?: string;
    user_agent?: string;
    login_at?: number;
    created_at: number;
  }>>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'profile' | 'settings' | 'security' | 'billing'>('profile');

  useEffect(() => {
    loadSettings();
    loadLoginHistory();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.getSettings();
      setSettings(data);
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadLoginHistory = async () => {
    try {
      const data = await api.getLoginHistory();
      setLoginHistory(data);
    } catch (err) {
      console.error('Failed to load login history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!displayName.trim()) return;
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      await api.updateProfile(displayName.trim());
      await refreshSession();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Cập nhật thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      await api.updateSettings(settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Cập nhật cài đặt thất bại');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('vi-VN');
  };

  const getDeviceInfo = (device: any) => {
    if (!device) return 'Thiết bị không xác định';
    const parts = [];
    if (device.browser) parts.push(device.browser);
    if (device.os) parts.push(device.os);
    if (device.device) parts.push(device.device);
    return parts.join(' · ') || 'Thiết bị không xác định';
  };

  const getLocationInfo = (item: any) => {
    const parts = [];
    if (item.city) parts.push(item.city);
    if (item.region) parts.push(item.region);
    if (item.country) parts.push(item.country);
    return parts.join(', ') || 'Không xác định';
  };

  if (!user) {
    return null; // ProtectedRoute handles redirect
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text mb-2">Bảng điều khiển</h1>
          <p className="text-text-muted">Quản lý tài khoản và cài đặt của bạn</p>
        </div>

        {/* Tab Navigation */}
        <div className="card overflow-hidden">
          <nav className="flex border-b border-border" role="tablist">
            {[
              { id: 'profile', label: 'Hồ sơ', icon: UserIcon },
              { id: 'settings', label: 'Cài đặt', icon: SettingsIcon },
              { id: 'security', label: 'Bảo mật', icon: ShieldIcon },
              { id: 'billing', label: 'Thanh toán', icon: CreditCardIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:text-text hover:border-border'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="p-6">
            {saveSuccess && (
              <div className="mb-4 p-3 text-sm text-green-600 bg-green-50 rounded-lg" role="alert">
                Đã lưu thay đổi thành công
              </div>
            )}
            {saveError && (
              <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 rounded-lg" role="alert">
                {saveError}
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-primary-light flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary">
                      {user.displayName?.[0]?.toUpperCase() || user.email[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-text">{user.displayName || 'Chưa đặt tên'}</h2>
                    <p className="text-text-muted">{user.email}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span
                        className={`px-3 py-1 text-sm font-medium rounded-full ${
                          user.planId === 'free'
                            ? 'bg-gray-100 text-gray-700'
                            : user.planId === 'plus'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {plan?.displayName || user.planId.toUpperCase()}
                      </span>
                      {user.role !== 'user' && (
                        <span className="px-3 py-1 text-sm font-medium rounded-full bg-amber-100 text-amber-700">
                          {user.role}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-medium text-text mb-4">Thông tin cá nhân</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="label">Tên hiển thị</label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="input w-full max-w-md mt-1"
                        placeholder="Nhập tên hiển thị"
                      />
                    </div>
                    <div>
                      <label className="label">Email</label>
                      <input
                        type="email"
                        value={user.email}
                        className="input w-full max-w-md mt-1 bg-gray-50"
                        disabled
                      />
                      <p className="text-xs text-text-muted mt-1">Email không thể thay đổi. Liên hệ hỗ trợ nếu cần thay đổi.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="label">Gói hiện tại</label>
                        <p className="text-text mt-1">{plan?.displayName || user.planId}</p>
                      </div>
                      <div>
                        <label className="label">Vai trò</label>
                        <p className="text-text mt-1 capitalize">{user.role}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving || !displayName.trim()}
                      className="btn-primary"
                    >
                      {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-text mb-4">Cài đặt ứng dụng</h3>
                {settingsLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <label className="label">Ngôn ngữ</label>
                      <select
                        value={settings.language || 'vi'}
                        onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                        className="input w-full max-w-xs mt-1"
                      >
                        <option value="vi">Tiếng Việt</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Múi giờ</label>
                      <select
                        value={settings.timezone || 'Asia/Ho_Chi_Minh'}
                        onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                        className="input w-full max-w-xs mt-1"
                      >
                        <option value="Asia/Ho_Chi_Minh">GMT+7 (Hà Nội, TP.HCM)</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Định dạng ngày</label>
                      <select
                        value={settings.date_format || 'DD/MM/YYYY'}
                        onChange={(e) => setSettings({ ...settings, date_format: e.target.value })}
                        className="input w-full max-w-xs mt-1"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Định dạng tiền tệ</label>
                      <select
                        value={settings.currency_format || 'VND'}
                        onChange={(e) => setSettings({ ...settings, currency_format: e.target.value })}
                        className="input w-full max-w-xs mt-1"
                      >
                        <option value="VND">VND (₫)</option>
                        <option value="USD">USD ($)</option>
                      </select>
                    </div>
                    <button
                      onClick={handleSaveSettings}
                      disabled={saving}
                      className="btn-primary"
                    >
                      {saving ? 'Đang lưu...' : 'Lưu cài đặt'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-text mb-4">Lịch sử đăng nhập</h3>
                  {historyLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
                    </div>
                  ) : loginHistory.length === 0 ? (
                    <div className="text-center py-8 text-text-muted">
                      Chưa có lịch sử đăng nhập
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-text-muted border-b border-border">
                            <th className="pb-2 font-medium">Thời gian</th>
                            <th className="pb-2 font-medium">Thiết bị</th>
                            <th className="pb-2 font-medium">Vị trí</th>
                            <th className="pb-2 font-medium">IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginHistory.map((item, index) => (
                            <tr key={index} className="border-b border-border/50">
                              <td className="py-3 text-text">{formatDate(item.login_at || item.created_at)}</td>
                              <td className="py-3 text-text-muted">{getDeviceInfo(item.device_info)}</td>
                              <td className="py-3 text-text-muted">{getLocationInfo(item)}</td>
                              <td className="py-3 text-text-muted font-mono">{item.ip || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-medium text-text mb-4">Đổi mật khẩu</h3>
                  <p className="text-text-muted mb-4">
                    Để đổi mật khẩu, vui lòng sử dụng tính năng "Quên mật khẩu" tại trang đăng nhập.
                  </p>
                  <a href="/login" className="btn-outline">
                    Đi đến trang đăng nhập
                  </a>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-medium text-text mb-4">Phiên hoạt động</h3>
                  <p className="text-text-muted mb-4">
                    Đăng xuất khỏi tất cả các thiết bị khác (trừ thiết bị hiện tại).
                  </p>
                  <button
                    onClick={logout}
                    className="btn-outline text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Đăng xuất tất cả thiết bị
                  </button>
                </div>
              </div>
            )}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-text mb-4">Thông tin thanh toán</h3>
                <div className="card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium text-text">{plan?.displayName || user.planId}</h4>
                      <p className="text-text-muted text-sm">Gói hiện tại</p>
                    </div>
                    <span
                      className={`px-3 py-1 text-sm font-medium rounded-full ${
                        user.planId === 'free'
                          ? 'bg-gray-100 text-gray-700'
                          : user.planId === 'plus'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {user.planId.toUpperCase()}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-text-muted">Giá hàng tháng</p>
                      <p className="font-medium text-text">
                        {plan?.priceMonthly
                          ? new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(plan.priceMonthly)
                          : 'Miễn phí'}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-muted">Giá hàng năm</p>
                      <p className="font-medium text-text">
                        {plan?.priceYearly
                          ? new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(plan.priceYearly)
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-muted">Tính năng</p>
                      <p className="font-medium text-text">{plan?.features.length || 0} tính năng</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <a href="/pricing" className="btn-primary">
                      Xem bảng giá & Nâng cấp
                    </a>
                  </div>
                </div>

                {plan?.features && plan.features.length > 0 && (
                  <div>
                    <h4 className="font-medium text-text mb-3">Tính năng của gói</h4>
                    <div className="flex flex-wrap gap-2">
                      {plan.features.map((f) => (
                        <span
                          key={f}
                          className="px-3 py-1 text-sm bg-primary-light text-primary rounded-full"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon components
function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  );
}